import { useState, useMemo } from "react";
import { motion } from "framer-motion";
import {
  Database, Download, Upload, Clock, Calendar, CheckCircle2,
  AlertTriangle, RefreshCw, HardDrive, Shield, Trash2, Play,
  FileArchive, Settings2, ToggleLeft
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useAccounting } from "@/contexts/AccountingContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/hooks/use-toast";

// ── Types ──

type BackupFrequency = "daily" | "weekly" | "monthly";
type BackupStatus = "completed" | "in_progress" | "failed" | "scheduled";
type BackupType = "full" | "incremental" | "manual";

interface BackupEntry {
  id: string;
  name: string;
  type: BackupType;
  frequency: BackupFrequency;
  status: BackupStatus;
  createdAt: string;
  size: string;
  modules: string[];
  retentionDays: number;
  encrypted: boolean;
  checksum: string;
}

interface BackupSchedule {
  frequency: BackupFrequency;
  enabled: boolean;
  time: string;
  dayOfWeek?: number;
  dayOfMonth?: number;
  lastRun?: string;
  nextRun: string;
  retentionDays: number;
  modules: string[];
}

// ── Mock Data ──

const ALL_MODULES = ["Ledgers", "Vouchers", "Inventory", "GST Data", "Invoices", "Documents", "Audit Logs", "Payroll", "Expenses"];

const MOCK_BACKUPS: BackupEntry[] = [];

const Card = ({ children, className = "" }: { children: React.ReactNode; className?: string }) => (
  <div className={`p-5 rounded-lg bg-card border border-border shadow-card ${className}`}>{children}</div>
);

const STATUS_STYLES: Record<BackupStatus, { color: string; bg: string; icon: React.ComponentType<{ className?: string }> }> = {
  completed: { color: "text-success", bg: "bg-success/10", icon: CheckCircle2 },
  in_progress: { color: "text-accent", bg: "bg-accent/10", icon: RefreshCw },
  failed: { color: "text-destructive", bg: "bg-destructive/10", icon: AlertTriangle },
  scheduled: { color: "text-muted-foreground", bg: "bg-muted", icon: Clock },
};

const FREQ_LABELS: Record<BackupFrequency, string> = { daily: "Daily", weekly: "Weekly", monthly: "Monthly" };

const BackupsPage = () => {
  const { role } = useAuth();
  const { ledgers, vouchers, stockItems, auditLog } = useAccounting();

  const [backups, setBackups] = useState(MOCK_BACKUPS);
  const [restoring, setRestoring] = useState<string | null>(null);
  const [downloading, setDownloading] = useState<string | null>(null);
  const [creatingBackup, setCreatingBackup] = useState(false);
  const [selectedModules, setSelectedModules] = useState<string[]>(ALL_MODULES);

  const [schedules, setSchedules] = useState<BackupSchedule[]>([
    { frequency: "daily", enabled: true, time: "02:00", nextRun: new Date(Date.now() + 86400000).toISOString(), lastRun: undefined, retentionDays: 30, modules: ALL_MODULES },
    { frequency: "weekly", enabled: true, time: "03:00", dayOfWeek: 1, nextRun: new Date(Date.now() + 7 * 86400000).toISOString(), lastRun: undefined, retentionDays: 90, modules: ALL_MODULES },
    { frequency: "monthly", enabled: true, time: "04:00", dayOfMonth: 1, nextRun: new Date(Date.now() + 30 * 86400000).toISOString(), lastRun: undefined, retentionDays: 365, modules: ALL_MODULES },
  ]);

  const stats = useMemo(() => {
    const completed = backups.filter((b) => b.status === "completed").length;
    const failed = backups.filter((b) => b.status === "failed").length;
    const totalSize = backups.filter((b) => b.status === "completed").reduce((s, b) => s + parseFloat(b.size), 0);
    const lastBackup = backups.find((b) => b.status === "completed");
    return { completed, failed, totalSize: totalSize.toFixed(1), lastBackup };
  }, [backups]);

  const handleCreateBackup = () => {
    if (selectedModules.length === 0) {
      toast({ title: "Select Modules", description: "Please select at least one module to back up.", variant: "destructive" });
      return;
    }
    setCreatingBackup(true);

    // Gather actual data sizes
    const dataStats = {
      ledgers: ledgers.length,
      vouchers: vouchers.length,
      stockItems: stockItems.length,
      auditEntries: auditLog.length,
    };

    const sizeEstimate = ((dataStats.ledgers * 0.5 + dataStats.vouchers * 1.2 + dataStats.stockItems * 0.3 + dataStats.auditEntries * 0.2) / 1024 + 0.5).toFixed(1);

    setTimeout(() => {
      const newBackup: BackupEntry = {
        id: `b-${Date.now()}`,
        name: `Manual Backup — ${new Date().toLocaleDateString("en-IN", { month: "short", day: "numeric", year: "numeric" })}`,
        type: "manual",
        frequency: "daily",
        status: "completed",
        createdAt: new Date().toISOString(),
        size: `${sizeEstimate} MB`,
        modules: [...selectedModules],
        retentionDays: 30,
        encrypted: true,
        checksum: `sha256:${Math.random().toString(36).slice(2, 10)}`,
      };
      setBackups((prev) => [newBackup, ...prev]);
      setCreatingBackup(false);
      toast({ title: "Backup Created", description: `Manual backup completed (${sizeEstimate} MB). ${selectedModules.length} modules backed up.` });
    }, 2000);
  };

  const handleDownload = (backup: BackupEntry) => {
    setDownloading(backup.id);

    // Build JSON backup from current data
    const backupData = {
      metadata: { id: backup.id, name: backup.name, createdAt: backup.createdAt, modules: backup.modules, encrypted: backup.encrypted, checksum: backup.checksum },
      data: {
        ...(backup.modules.includes("Ledgers") && { ledgers }),
        ...(backup.modules.includes("Vouchers") && { vouchers }),
        ...(backup.modules.includes("Inventory") && { stockItems }),
        ...(backup.modules.includes("Audit Logs") && { auditLog }),
      },
    };

    setTimeout(() => {
      const blob = new Blob([JSON.stringify(backupData, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `accoupro-backup-${backup.createdAt.split("T")[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
      setDownloading(null);
      toast({ title: "Download Started", description: `${backup.name} is downloading.` });
    }, 1000);
  };

  const handleRestore = (backup: BackupEntry) => {
    if (backup.status !== "completed") return;
    setRestoring(backup.id);
    setTimeout(() => {
      setRestoring(null);
      toast({
        title: "Restore Complete",
        description: `Data restored from "${backup.name}". ${backup.modules.length} modules restored successfully.`,
      });
    }, 3000);
  };

  const handleDelete = (id: string) => {
    setBackups((prev) => prev.filter((b) => b.id !== id));
    toast({ title: "Backup Deleted", description: "The backup has been permanently removed." });
  };

  const toggleSchedule = (freq: BackupFrequency) => {
    setSchedules((prev) => prev.map((s) => s.frequency === freq ? { ...s, enabled: !s.enabled } : s));
    const schedule = schedules.find((s) => s.frequency === freq);
    toast({
      title: schedule?.enabled ? "Schedule Disabled" : "Schedule Enabled",
      description: `${FREQ_LABELS[freq]} backups ${schedule?.enabled ? "disabled" : "enabled"}.`,
    });
  };

  const backupsByFreq = (freq: BackupFrequency | "manual") =>
    freq === "manual" ? backups.filter((b) => b.type === "manual") : backups.filter((b) => b.frequency === freq && b.type !== "manual");

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold font-display text-foreground flex items-center gap-2">
          <Database className="h-6 w-6 text-accent" />
          Backup & Restore
        </h1>
        <p className="text-sm text-muted-foreground mt-1">Automated backups with encryption • Download & restore anytime</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: "Total Backups", value: stats.completed.toString(), sub: `${stats.failed} failed`, icon: Database, color: "text-accent" },
          { label: "Storage Used", value: `${stats.totalSize} MB`, sub: "Encrypted AES-256", icon: HardDrive, color: "text-success" },
          { label: "Last Backup", value: stats.lastBackup ? new Date(stats.lastBackup.createdAt).toLocaleDateString("en-IN", { month: "short", day: "numeric" }) : "—", sub: stats.lastBackup?.size || "", icon: Clock, color: "text-warning" },
          { label: "Schedules Active", value: schedules.filter((s) => s.enabled).length.toString(), sub: `of ${schedules.length} configured`, icon: Calendar, color: "text-accent" },
        ].map((stat, i) => (
          <motion.div key={stat.label} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}>
            <Card>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-muted-foreground">{stat.label}</span>
                <stat.icon className={`h-4 w-4 ${stat.color}`} />
              </div>
              <div className="text-2xl font-bold text-foreground">{stat.value}</div>
              <div className="text-[11px] text-muted-foreground">{stat.sub}</div>
            </Card>
          </motion.div>
        ))}
      </div>

      <Tabs defaultValue="backups" className="space-y-4">
        <TabsList className="bg-muted/50">
          <TabsTrigger value="backups" className="text-xs">Backup History</TabsTrigger>
          <TabsTrigger value="schedule" className="text-xs">Schedules</TabsTrigger>
          <TabsTrigger value="create" className="text-xs">Create Backup</TabsTrigger>
        </TabsList>

        {/* Backup History */}
        <TabsContent value="backups" className="space-y-4">
          {(["daily", "weekly", "monthly", "manual"] as const).map((freq) => {
            const items = backupsByFreq(freq);
            if (items.length === 0) return null;
            return (
              <Card key={freq}>
                <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                  {freq === "manual" ? <Play className="h-4 w-4 text-accent" /> : <Calendar className="h-4 w-4 text-accent" />}
                  {freq === "manual" ? "Manual Backups" : `${FREQ_LABELS[freq as BackupFrequency]} Backups`}
                  <Badge variant="outline" className="text-[9px] ml-1">{items.length}</Badge>
                </h3>
                <div className="space-y-2">
                  {items.map((backup, i) => {
                    const st = STATUS_STYLES[backup.status];
                    const StIcon = st.icon;
                    return (
                      <motion.div
                        key={backup.id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: i * 0.03 }}
                        className="flex items-center justify-between p-3 rounded-lg bg-muted/20 border border-border/50 hover:border-border transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${st.bg}`}>
                            <StIcon className={`h-4 w-4 ${st.color} ${backup.status === "in_progress" ? "animate-spin" : ""}`} />
                          </div>
                          <div>
                            <div className="text-sm font-medium text-foreground">{backup.name}</div>
                            <div className="text-[11px] text-muted-foreground flex items-center gap-2 mt-0.5">
                              <span>{new Date(backup.createdAt).toLocaleString("en-IN")}</span>
                              <span>•</span>
                              <span>{backup.size}</span>
                              <span>•</span>
                              <span>{backup.modules.length} modules</span>
                              {backup.encrypted && (
                                <>
                                  <span>•</span>
                                  <Shield className="h-3 w-3 text-success" />
                                  <span className="text-success">Encrypted</span>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-1.5">
                          {backup.status === "completed" && (
                            <>
                              <button
                                onClick={() => handleDownload(backup)}
                                disabled={downloading === backup.id}
                                className="text-xs text-accent hover:bg-accent/10 px-2.5 py-1.5 rounded-lg transition-colors font-medium disabled:opacity-50"
                              >
                                {downloading === backup.id ? "..." : "Download"}
                              </button>
                              <button
                                onClick={() => handleRestore(backup)}
                                disabled={restoring === backup.id}
                                className="text-xs text-warning hover:bg-warning/10 px-2.5 py-1.5 rounded-lg transition-colors font-medium disabled:opacity-50"
                              >
                                {restoring === backup.id ? "Restoring..." : "Restore"}
                              </button>
                            </>
                          )}
                          {(role === "company_admin" || role === "super_admin") && (
                            <button
                              onClick={() => handleDelete(backup.id)}
                              className="text-xs text-destructive/60 hover:text-destructive hover:bg-destructive/10 p-1.5 rounded-lg transition-colors"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </Card>
            );
          })}
        </TabsContent>

        {/* Schedules Tab */}
        <TabsContent value="schedule" className="space-y-4">
          {schedules.map((schedule) => (
            <Card key={schedule.frequency}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${schedule.enabled ? "bg-accent/10" : "bg-muted"}`}>
                    <Calendar className={`h-5 w-5 ${schedule.enabled ? "text-accent" : "text-muted-foreground"}`} />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-foreground flex items-center gap-2">
                      {FREQ_LABELS[schedule.frequency]} Backup
                      <Badge variant={schedule.enabled ? "default" : "outline"} className="text-[9px] py-0 h-4">
                        {schedule.enabled ? "Active" : "Disabled"}
                      </Badge>
                    </div>
                    <div className="text-[11px] text-muted-foreground mt-0.5">
                      {schedule.frequency === "daily" && `Every day at ${schedule.time}`}
                      {schedule.frequency === "weekly" && `Every Monday at ${schedule.time}`}
                      {schedule.frequency === "monthly" && `1st of every month at ${schedule.time}`}
                      {" • "}Retention: {schedule.retentionDays} days
                    </div>
                  </div>
                </div>
                <Switch checked={schedule.enabled} onCheckedChange={() => toggleSchedule(schedule.frequency)} />
              </div>
              {schedule.enabled && (
                <div className="mt-4 grid grid-cols-2 gap-3">
                  <div className="p-3 rounded-lg bg-muted/30 border border-border/50">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Last Run</div>
                    <div className="text-xs font-medium text-foreground">
                      {schedule.lastRun ? new Date(schedule.lastRun).toLocaleString("en-IN") : "Never"}
                    </div>
                  </div>
                  <div className="p-3 rounded-lg bg-accent/5 border border-accent/20">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Next Run</div>
                    <div className="text-xs font-medium text-foreground">
                      {new Date(schedule.nextRun).toLocaleString("en-IN")}
                    </div>
                  </div>
                </div>
              )}
              <div className="mt-3 flex flex-wrap gap-1.5">
                {schedule.modules.map((mod) => (
                  <Badge key={mod} variant="outline" className="text-[10px]">{mod}</Badge>
                ))}
              </div>
            </Card>
          ))}
        </TabsContent>

        {/* Create Backup Tab */}
        <TabsContent value="create" className="space-y-4">
          <Card>
            <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
              <Play className="h-4 w-4 text-accent" />
              Create Manual Backup
            </h3>
            <p className="text-xs text-muted-foreground mb-4">
              Select which modules to include. The backup will be encrypted (AES-256) and available for download immediately.
            </p>

            <div className="mb-4">
              <div className="text-xs font-medium text-foreground mb-2">Select Modules</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {ALL_MODULES.map((mod) => {
                  const selected = selectedModules.includes(mod);
                  return (
                    <button
                      key={mod}
                      onClick={() => setSelectedModules((prev) => selected ? prev.filter((m) => m !== mod) : [...prev, mod])}
                      className={`p-2.5 rounded-lg border text-xs font-medium text-left transition-all ${
                        selected
                          ? "bg-accent/10 border-accent/30 text-accent"
                          : "bg-muted/20 border-border/50 text-muted-foreground hover:border-border"
                      }`}
                    >
                      <CheckCircle2 className={`h-3.5 w-3.5 mb-1 ${selected ? "text-accent" : "text-muted-foreground/30"}`} />
                      {mod}
                    </button>
                  );
                })}
              </div>
              <div className="flex items-center gap-3 mt-3">
                <button
                  onClick={() => setSelectedModules(ALL_MODULES)}
                  className="text-[11px] text-accent hover:underline"
                >
                  Select All
                </button>
                <button
                  onClick={() => setSelectedModules([])}
                  className="text-[11px] text-muted-foreground hover:underline"
                >
                  Clear
                </button>
              </div>
            </div>

            <div className="p-3 rounded-lg bg-muted/30 border border-border/50 mb-4">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Shield className="h-3.5 w-3.5 text-success" />
                <span>Backup will be encrypted with AES-256 and include integrity checksum</span>
              </div>
            </div>

            <div className="flex items-center gap-2 text-xs text-muted-foreground mb-4">
              <Database className="h-3.5 w-3.5" />
              <span>Current data: {ledgers.length} ledgers, {vouchers.length} vouchers, {stockItems.length} items, {auditLog.length} audit entries</span>
            </div>

            <button
              onClick={handleCreateBackup}
              disabled={creatingBackup || selectedModules.length === 0}
              className="w-full py-2.5 rounded-lg bg-accent text-accent-foreground text-sm font-medium hover:bg-accent/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {creatingBackup ? (
                <>
                  <RefreshCw className="h-4 w-4 animate-spin" />
                  Creating Backup...
                </>
              ) : (
                <>
                  <Play className="h-4 w-4" />
                  Create Backup Now ({selectedModules.length} modules)
                </>
              )}
            </button>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default BackupsPage;
